import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { useInput } from "@redotech/react-util/form";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { FormTextInput, InputTheme } from "@redotech/redo-web/text-input";
import { input } from "@redotech/ui/form";
import { memo, ClipboardEvent as ReactClipboardEvent, useEffect } from "react";
import { normalizePastedRateTableCell, RateCardForm } from "./rate-table-utils";
import * as styles from "./rate-table.module.css";

// We use this to represent the top left cell in the table. We never want it to have a value, but we want it to be there so that we can use it to copy/paste
const stringInput = input<string>();

export interface RateTableColumnHeader {
  label: string;
  deletable: boolean;
}

export const LabelRateTableForm = memo(function LabelRateTableForm({
  input,
  deleteRow,
  deleteColumn,
  yAxisLabel,
  xAxisLabel,
  columnHeaders,
  readonly = false,
  omitHeaderRow,
}: {
  input: RateCardForm;
  deleteRow: (index: number) => void;
  deleteColumn?: (index: number) => void;
  yAxisLabel: string;
  xAxisLabel: string;
  /** Per-column headers (label + deletable) replacing the repeated xAxisLabel,
   * e.g. country names + a pinned rest-of-world column. */
  columnHeaders?: RateTableColumnHeader[];
  readonly?: boolean;
  /** True if the first row contains regular data instead of specially formatted xAxis labels */
  omitHeaderRow?: boolean;
}) {
  const overridePaste = (
    event: ReactClipboardEvent,
    columnIndex: number,
    rowIndex: number,
    skipCell?: boolean,
  ) => {
    event.preventDefault();
    const data = event.clipboardData.getData("text");
    const grid = data.split("\n").map((row) => row.split("\t"));

    const normalized = grid.map((row) => row.map(normalizePastedRateTableCell));

    const tableData = [...input.value.data];

    for (const [i, element] of normalized.entries()) {
      for (const [j, element_] of element.entries()) {
        // Don't paste into the top left cell - we don't ever want that to have visible data
        if (skipCell && j === 0 && i === 0) {
          continue;
        }
        const existingCell = tableData.at(rowIndex + i)?.at(columnIndex + j);
        if (existingCell) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          tableData[rowIndex + i][columnIndex + j] = element_;
        }
      }
    }

    input.setValue({
      data: tableData,
      defaultValue: input.value.defaultValue,
      defaultValuePercentage: input.value.defaultValuePercentage,
      useDefaultValuePercentage: input.value.useDefaultValuePercentage,
    });
  };

  // We use this to represent the top left cell in the table. We never want it to have a value, but we want it to be there so that we can use it to copy/paste
  const upperLeftCellInput = useInput(stringInput, "");

  // We don't want to disable the fake input because that would prevent us from pasting into the table from there
  // So instead if someone changes it we just reset it to an empty string
  useEffect(() => {
    upperLeftCellInput.setValue("");
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upperLeftCellInput.value.length]);

  return (
    <Flex dir="column" w="full">
      <fieldset disabled={readonly}>
        <table className={styles.rateTable}>
          <thead>
            <tr>
              <th />
              <th>{yAxisLabel}</th>
              {input.inputs.data.inputs.at(0)?.inputs.map((_, index) => {
                if (index === 0) {
                  return false;
                }
                const header = columnHeaders?.at(index - 1);
                const label = header ? header.label : xAxisLabel;
                const canDelete = header ? header.deletable : true;
                return (
                  <th key={index}>
                    {deleteColumn && canDelete ? (
                      <RedoButton
                        IconLeading={TrashIcon}
                        onClick={() => deleteColumn?.(index)}
                        size="sm"
                        text={label}
                        theme="normal"
                      />
                    ) : (
                      label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {input.inputs.data.inputs.map((rowInput, rowIndex) => (
              <tr key={rowIndex}>
                {/* Headerless grids keep their last row: deleting it would drop
                    the column shape and wedge the editor. */}
                {rowIndex > 0 ||
                (omitHeaderRow && input.inputs.data.inputs.length > 1) ? (
                  <td>
                    <RedoButton
                      IconLeading={TrashIcon}
                      onClick={() => deleteRow(rowIndex)}
                      size="sm"
                      text=""
                      theme="normal"
                    />
                  </td>
                ) : (
                  <td />
                )}
                {rowInput.inputs.map((cellInput, columnIndex) => (
                  <td key={`${rowIndex}-${columnIndex}`}>
                    {rowIndex > 0 || columnIndex > 0 || omitHeaderRow ? (
                      <FormTextInput
                        input={cellInput}
                        label=""
                        labeledInputClassName={styles.rateTableInput}
                        // min={0} // temporarily allow negative numbers to make up for invalid USPS rates
                        onPaste={(event: ReactClipboardEvent) => {
                          overridePaste(event, columnIndex, rowIndex);
                        }}
                        step="any"
                        theme={InputTheme.TABLE}
                        type="number"
                      />
                    ) : (
                      <FormTextInput
                        input={upperLeftCellInput}
                        label=""
                        labeledInputClassName={styles.rateTableInput}
                        onPaste={(event: ReactClipboardEvent) => {
                          overridePaste(event, columnIndex, rowIndex, true);
                        }}
                        theme={InputTheme.TABLE}
                        type="text"
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>
    </Flex>
  );
});
